import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, RefreshCw } from "lucide-react";
import { ChannelsPanel } from "@/components/radar_sync/ChannelsPanel";
import { EnvironmentsPanel } from "@/components/radar_sync/EnvironmentsPanel";
import { ResetApprovalsPanel } from "@/components/radar_sync/ResetApprovalsPanel";
import { SpokeStatusCard } from "@/components/radar_sync/SpokeStatusCard";
import { LinkRequestsPanel } from "@/components/radar_sync/LinkRequestsPanel";
import { LinkToHubCard } from "@/components/radar_sync/LinkToHubCard";

const num = (n) => (n == null ? "—" : Number(n).toLocaleString());

/** Settings → Radar Sync. Hub: channels · link requests · environments · reset approvals. Spoke: link + two-way sync status. */
export default function RadarSyncPage() {
    const { user } = useAuth();
    const canWrite = user?.role === "super_admin";
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/sync/overview");
            setData(r.data); setErr(null);
        } catch (e) {
            setErr(formatApiErrorDetail(e?.response?.data?.detail));
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    return (
        <div className="px-4 sm:px-8 py-8 max-w-6xl" data-testid="radar-sync-page">
            <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="label-xs text-[#7C3AED]">Configuration</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Radar Sync</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        {data?.mode === "hub"
                            ? "This deployment is the hub: every linked environment syncs two-way with it — local changes come up, hub data goes down to every channel (dev · stage · prod), the hub wins on conflict. Reconciled twice a day."
                            : data?.status?.has_hub_link
                                ? "This deployment is linked to the hub: its Radar dataset syncs two-way every 5 minutes (hub wins on conflict) and is reconciled with the hub twice a day."
                                : "This deployment is not linked to a hub yet: link it below and it downloads the hub's Radar dataset automatically, then keeps it in sync two-way."}
                    </p>
                    {data && (
                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-white/50">
                            <span className="rounded border border-white/10 px-2 py-0.5" data-testid="sync-mode-pill">mode <span className="text-white/90">{data.mode}</span></span>
                            <span className="rounded border border-white/10 px-2 py-0.5" data-testid="sync-env-pill">{data.env_name}</span>
                            {data.mode === "hub" && <span className="rounded border border-white/10 px-2 py-0.5 font-mono" data-testid="sync-live-seq">live seq {num(data.live_seq)}</span>}
                            {data.hub_url && <span className="rounded border border-white/10 px-2 py-0.5 font-mono">hub {data.hub_url}</span>}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <a href="/downloads/radar-sync-operations.md" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded border border-white/10 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5" data-testid="radar-sync-guide-link">
                        <BookOpen className="w-3.5 h-3.5" /> Ops guide
                    </a>
                    <button onClick={load} className="inline-flex items-center gap-1.5 rounded border border-white/10 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5" data-testid="radar-sync-refresh-btn">
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </button>
                </div>
            </div>

            {err && <div className="text-xs text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded px-3 py-2 mb-4" data-testid="radar-sync-error">{err}</div>}
            {!data && !err && <div className="text-xs text-white/40 font-mono" data-testid="radar-sync-loading">Loading…</div>}

            {data?.mode === "hub" && (
                <div className="space-y-4">
                    <ChannelsPanel channels={data.channels} promotions={data.promotions} />
                    <LinkRequestsPanel requests={data.link_requests} canWrite={canWrite} onChanged={load} />
                    <EnvironmentsPanel environments={data.environments} imports={data.imports} canWrite={canWrite} onChanged={load} />
                    <ResetApprovalsPanel resets={data.resets} canWrite={canWrite} onChanged={load} />
                    {!canWrite && <div className="text-[11px] text-white/40" data-testid="radar-sync-readonly-note">Environment keys, link approvals, imports and reset decisions require a super admin.</div>}
                </div>
            )}
            {data && data.mode !== "hub" && (
                <div className="space-y-4">
                    <LinkToHubCard link={data.link} canWrite={canWrite} onChanged={load} />
                    {data.status?.has_hub_link
                        ? <SpokeStatusCard status={data.status} onRefresh={load} canWrite={canWrite} />
                        : <StandaloneCard status={data.status} />}
                    <ResetStateCard reset={data.reset} />
                </div>
            )}
        </div>
    );
}

function StandaloneCard({ status }) {
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6 text-xs text-white/60" data-testid="standalone-card">
            <div className="font-display text-lg text-white tracking-tight mb-2">Not linked</div>
            <p>Radar data is produced here only. Link this environment to the hub (above) — or configure <code>RADAR_HUB_URL</code> + <code>RADAR_SYNC_KEY</code> — and it downloads the hub's dataset automatically, pushes its own changes up and stays in sync (hub wins on conflict).</p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                {(status?.collections || []).map((c) => <div key={c.coll} className="rounded border border-white/10 bg-[#050505] px-3 py-2"><div className="label-xs">{c.label}</div><div className="font-mono text-white/80">{num(status.counts?.[c.coll])}</div></div>)}
            </div>
        </div>
    );
}

function ResetStateCard({ reset }) {
    if (!reset) return null;
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6 text-xs text-white/60" data-testid="reset-state-card">
            <div className="font-display text-lg text-white tracking-tight mb-2">Reset to blank</div>
            {reset.state === "not_available"
                ? <p>Not available on this deployment ({reset.reason === "hub" ? "the hub cannot be reset through this flow" : "no hub link configured"}).</p>
                : <p>Current state: <span className="font-mono text-white/90" data-testid="reset-state-value">{reset.state}</span>{reset.request_id ? ` · ${reset.request_id}` : ""}. Requests are made on <a href="/init" className="underline">/init</a> by a super admin of this environment and need hub approval.</p>}
        </div>
    );
}
